/**
 * Encode and decode structured column filter values in the URL.
 *
 * The `cf_` convention is preserved and kept readable so links stay shareable:
 *   text       cf_name=acme
 *   dateRange  cf_start_date=2026-01-01..2026-03-31   (either bound may be empty)
 *   numberRange cf_amount=100..500.5                  (either bound may be empty)
 *   select     cf_status=Active,Inactive              (commas in a value are encoded)
 *   blank      cf_end_date=(blank)                    (any non-select column)
 *
 * Decoding is deliberately LENIENT (FR-015a): an unusable value is dropped and
 * the rest of the state still loads, so a hand-edited or stale link degrades
 * rather than locking the user out. The API is strict about the same value and
 * answers 400 — two different paths, intentionally not unified.
 */

import type { ColumnFilterValue, ColumnFilterType } from '@app-types';

export const COLUMN_FILTER_PREFIX = 'cf_';

const RANGE_SEPARATOR = '..';

/**
 * A blank filter in the URL. Parenthesised so that a text search for the word
 * "blank" stays a text search.
 */
const BLANK_TOKEN = '(blank)';

/** Reserved selection value meaning "no assignment at all", as the API names it. */
export const UNASSIGNED_FILTER_VALUE = 'unassigned';

/**
 * Longest text value the Contract list API accepts in a column filter; it
 * answers 400 beyond this. Mirrors the API's own limit (MAX_VALUE_LENGTH in
 * srg-backend api/validators/helpers/contract-column-filters.ts) — keep the two
 * in step.
 */
export const CONTRACT_TEXT_FILTER_MAX_LENGTH = 200;

/** A decimal number, optionally signed — the same form the API accepts. */
const DECIMAL_NUMBER = /^-?\d+(\.\d+)?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day, so 2026-02-30 is rejected rather than rolled over. */
function isRealCalendarDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export function encodeFilterValue(value: ColumnFilterValue): string | null {
  if (typeof value === 'string') {
    return value.trim() === '' ? null : value;
  }

  if (value.kind === 'text') {
    return value.value.trim() === '' ? null : value.value;
  }

  if (value.kind === 'blank') return BLANK_TOKEN;

  if (value.kind === 'dateRange' || value.kind === 'numberRange') {
    const from = value.from ?? '';
    const to = value.to ?? '';
    if (from === '' && to === '') return null;
    return `${from}${RANGE_SEPARATOR}${to}`;
  }

  const values = value.values.filter((entry) => entry.trim() !== '');
  if (values.length === 0) return null;

  // A value containing the separator would otherwise split into two on decode.
  return values.map((entry) => entry.replace(/,/g, '%2C')).join(',');
}

/** Why a value from the URL could not be used, for the notice shown to the user. */
export interface DroppedFilter {
  columnKey: string;
  reason: string;
}

/**
 * Decode one `cf_*` parameter for a column of the given type.
 *
 * Returns the value, or a reason it was dropped. Never throws: a bad value in a
 * shared link must not block access to the list.
 */
export function decodeFilterValue(
  columnKey: string,
  raw: string,
  filterType: ColumnFilterType,
  /** Whether the column offers a no-value filter; only then is the token one. */
  acceptsBlank = false,
  /** Longest text value the column's API accepts; absent means no limit. */
  maxLength?: number
): { value: ColumnFilterValue } | { dropped: DroppedFilter } {
  // A select column says "no value" with its own UNASSIGNED_FILTER_VALUE option.
  // Elsewhere the token falls through: text keeps it as typed, a range drops it.
  if (raw === BLANK_TOKEN && acceptsBlank && filterType !== 'select') {
    return { value: { kind: 'blank' } };
  }

  if (filterType === 'dateRange') {
    if (!raw.includes(RANGE_SEPARATOR)) {
      return { dropped: { columnKey, reason: 'is not a valid date range' } };
    }

    const [from, to] = raw.split(RANGE_SEPARATOR);
    if (from === '' && to === '') {
      return { dropped: { columnKey, reason: 'has no dates' } };
    }
    if (from !== '' && !isRealCalendarDate(from)) {
      return { dropped: { columnKey, reason: `has an invalid "from" date (${from})` } };
    }
    if (to !== '' && !isRealCalendarDate(to)) {
      return { dropped: { columnKey, reason: `has an invalid "to" date (${to})` } };
    }
    if (from !== '' && to !== '' && to < from) {
      return { dropped: { columnKey, reason: 'ends before it starts' } };
    }

    return {
      value: {
        kind: 'dateRange',
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
      },
    };
  }

  if (filterType === 'numberRange') {
    if (!raw.includes(RANGE_SEPARATOR)) {
      return { dropped: { columnKey, reason: 'is not a valid number range' } };
    }

    const [from, to] = raw.split(RANGE_SEPARATOR);
    if (from === '' && to === '') {
      return { dropped: { columnKey, reason: 'has no numbers' } };
    }
    for (const [bound, entry] of [
      ['from', from],
      ['to', to],
    ] as const) {
      if (entry !== '' && !DECIMAL_NUMBER.test(entry)) {
        return { dropped: { columnKey, reason: `has a non-numeric "${bound}" value (${entry})` } };
      }
    }
    if (from !== '' && to !== '' && Number(to) < Number(from)) {
      return { dropped: { columnKey, reason: 'has a maximum below its minimum' } };
    }

    return {
      value: {
        kind: 'numberRange',
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
      },
    };
  }

  if (filterType === 'select') {
    const values = raw
      .split(',')
      .map((entry) => entry.replace(/%2C/gi, ','))
      .filter((entry) => entry.trim() !== '');

    if (values.length === 0) {
      return { dropped: { columnKey, reason: 'has no selected values' } };
    }

    return { value: { kind: 'anyOf', values } };
  }

  if (raw.trim() === '') {
    return { dropped: { columnKey, reason: 'is empty' } };
  }

  if (maxLength !== undefined && raw.length > maxLength) {
    return { dropped: { columnKey, reason: `is longer than ${maxLength} characters` } };
  }

  return { value: { kind: 'text', value: raw } };
}

/** Human-readable value for an active-filter indicator. */
export function describeFilterValue(value: ColumnFilterValue): string {
  if (typeof value === 'string') return value;
  if (value.kind === 'text') return value.value;
  if (value.kind === 'anyOf') return value.values.join(', ');
  if (value.kind === 'blank') return 'Blank';

  const from = value.from ?? '';
  const to = value.to ?? '';
  if (from && to) return `${from} to ${to}`;
  if (from) return `from ${from}`;
  return `until ${to}`;
}

/** Whether a value actually narrows the list, as opposed to being cleared. */
export function isActiveFilterValue(value: ColumnFilterValue | undefined): boolean {
  if (value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (value.kind === 'text') return value.value.trim() !== '';
  if (value.kind === 'anyOf') return value.values.some((entry) => entry.trim() !== '');
  if (value.kind === 'blank') return true;

  return Boolean(value.from) || Boolean(value.to);
}
